import { atom, observe } from "@mdotm/shadcn/legacy/reactivity";

/**
 * Vertical envelope of a page — owned by the page-layout layer ({@link PageLayout}), not by page CSS.
 * - `"auto"`: the page grows with its content and the window scrolls (default).
 * - `"screen"`: the page is capped to the viewport and never body-scrolls — for fixed-height
 *   workspaces whose inner regions scroll on their own.
 * - `{ capUntilWidth }`: responsive. Behaves like `"screen"` while the page content area is at least
 *   `capUntilWidth` px wide, and like `"auto"` once it is narrower. Lets a page keep a fixed-height
 *   workspace on wide screens but become a normal scrolling page when its panels stack.
 *
 * In every case {@link PageLayout} publishes the resolved fit as `data-page-fit="screen" | "grow"` on
 * the page content element, so page CSS can react to the *layout layer's* decision instead of
 * re-deriving a breakpoint of its own.
 */
export type MaxPageHeight = "auto" | "screen" | { capUntilWidth: number };

export type PlatformLayoutOptions = {
	sideBar: boolean;
	mode: "default" | "print";
	/**
	 * @default auto
	 */
	maxPageHeight: MaxPageHeight;
	/**
	 * @default true
	 */
	padding?: boolean;
	/**
	 * Optional content-area width envelope (px). Owned here so a page declares its width intent
	 * through the layout contract rather than hand-rolling width CSS. `maxWidth` also centers the
	 * content. Leave undefined for the platform default (full width, floored by the shell min-width).
	 */
	minWidth?: number;
	maxWidth?: number;
};

export const platformLayoutOptions$ = atom<PlatformLayoutOptions>({
	sideBar: true,
	mode: "default",
	maxPageHeight: "auto",
});

/**
 * Mirrors {@link PlatformLayoutOptions.mode} onto the root element as `data-page-mode`, so global
 * CSS can react to it. The platform's own chrome is inside the page tree and can be conditioned on
 * props, but third-party overlays are appended to `<body>` — out of reach of any page-scoped
 * selector — and the iubenda consent banner is one of them: `print` routes hide it that way, both
 * in the PDF export and for anyone previewing the URL in a browser.
 *
 * Kept next to the atom rather than in a component so the attribute cannot fall out of sync with
 * the source of truth, whichever route (or non-React caller) writes it.
 */
observe((get) => {
	document.documentElement.dataset.pageMode = get(platformLayoutOptions$).mode;
});
